/**
 * Rótulos, cores e formatação da tela de Cancelamentos. Compartilhados entre a
 * lista (CancelamentosView) e o modal da solicitação — o mesmo selo com duas
 * cores diferentes nas duas pontas é o tipo de coisa que confunde quem trata.
 */
import type { StatusSolicitacaoCancelamento } from '../../src/lib/cancelamentos';

export const fmtKwh = (v: number | null) => v === null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`;
export const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
export const fmtData = (v: string | null) => {
    if (!v) return '—';
    const [ano, mes, dia] = v.slice(0, 10).split('-');
    return `${dia}/${mes}/${ano}`;
};

export const STATUS_SOLICITACAO_CLS: Record<StatusSolicitacaoCancelamento, string> = {
    aberta: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200/80 dark:border-amber-800/40',
    em_andamento: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200/80 dark:border-blue-800/40',
    concluida: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200/80 dark:border-emerald-800/40',
    cancelada: 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200/80 dark:border-slate-700/80',
};

export const DISTRATO_META: Record<string, { label: string; cls: string }> = {
    sem_distrato: { label: 'Sem distrato gerado', cls: 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200/80 dark:border-slate-700/80' },
    ativo: { label: 'Distrato ativo', cls: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200/80 dark:border-blue-800/40' },
    cancelado: { label: 'Distrato cancelado', cls: 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400 border-gray-200/80 dark:border-slate-700/80' },
};

/**
 * Distrato feito fora do sistema (importado da planilha). Só aparece quando
 * não há termo em `termo_cancelamento_logs` — o termo do sistema tem
 * precedência, para não haver duas fontes de verdade.
 */
export const DISTRATO_EXTERNO_LABEL: Record<'sim' | 'nao' | 'aguardando', string> = {
    sim: 'Distrato externo',
    nao: 'Sem distrato (informado)',
    aguardando: 'Externo — aguardando assinatura',
};

/** Só os estados que pedem atenção ganham selo; "no prazo" e "respondida" ficam só com a data. */
export const RETORNO_META: Record<string, { label: string; cls: string } | null> = {
    sem_prazo: null,
    respondida: null,
    no_prazo: null,
    vence_hoje: { label: 'Vence hoje', cls: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800/50' },
    vencida: { label: 'Vencido', cls: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/50' },
};

/** Multa contratual que não foi cobrada — o mesmo critério do KPI "Multa Não Cobrada". */
export const multaNaoCobrada = (multaSituacao: string | null) =>
    !!multaSituacao && /(NAO|NÃO)\s*(FOI\s*)?COBR/i.test(multaSituacao);

/** Classe base dos selos pequenos da coluna do cliente. */
export const SELO = 'inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border';

/** Link do "Gerenciar UC" já filtrado no cliente da linha, não na lista inteira. */
export const linkGerenciarUc = (ucNumero: string) => `#/financeiro/ucs?busca=${encodeURIComponent(ucNumero)}`;
